import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from 'react';
import { ChevronUp, ChevronDown, AlertCircle, AlertTriangle, Filter, Search, ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';
import { useResizableColumns } from '@/hooks/useResizableColumns';
import PaginationControls from '@/components/common/PaginationControls';
import { DEPARTMENTS, DISTRO_OPTIONS, isCriticalFlag } from './constants';
import DecisionDrawer from './DecisionDrawer';
import MatchIcon from './MatchIcon';
import { formatDistanceToNow, parseISO } from 'date-fns';

// Decision timestamps come back from Mongo/FastAPI as naive UTC — no 'Z' or
// offset suffix — even though they're written as tz-aware UTC; Mongo strips
// tzinfo on write. date-fns' parseISO treats an offset-less string as LOCAL
// time, so without this every timestamp in the tab drifts by the browser's
// UTC offset (e.g. an email received an hour ago showed as "in about 3
// hours" for an EDT browser). Treat any string missing a timezone
// designator as UTC before parsing.
function parseUtc(iso) {
  if (!iso) return null;
  const hasOffset = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso);
  return parseISO(hasOffset ? iso : `${iso}Z`);
}

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 15;
const PAGE_SIZE_STORAGE_KEY = 'boss.ai_dispatcher.decisions.page_size.v1';

// The list endpoint clamps `limit` to 500 (backend/routes/ai_dispatcher.py,
// list_decisions). Ask for the ceiling — see the paging note on fetchDecisions.
const FETCH_LIMIT = 500;

/**
 * Page size survives a reload rather than only the session. Someone who has
 * decided they want 100 rows has decided it about the screen, not about this
 * visit, and re-picking it every morning is the kind of small tax that makes
 * a review tool feel like a chore.
 *
 * Wrapped because localStorage throws outright in some embedded/iframe
 * contexts rather than returning null — the tab still has to render there.
 */
function loadPageSize() {
  try {
    const raw = parseInt(localStorage.getItem(PAGE_SIZE_STORAGE_KEY), 10);
    if (PAGE_SIZE_OPTIONS.includes(raw)) return raw;
  } catch {
    /* ignore — fall through to the default */
  }
  return DEFAULT_PAGE_SIZE;
}

function savePageSize(size) {
  try {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
  } catch {
    /* ignore — a preference that fails to persist is not worth an error */
  }
}

// Absolute time, in whatever timezone and locale the viewer's browser is set
// to (`undefined` locale = the runtime default; no timeZone is passed, so the
// runtime's own zone is used). Deliberately not a relative string: "about 7
// hours" used to be the primary value here, and it was concealing a real
// defect — some rows carry a received_at that has not happened yet, which
// date-fns rendered as "in about 7 hours", reading like a schedule rather
// than a bug.
const RECEIVED_FMT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

// Tooltip form. Seconds and the zone name, so a screenshot of this tab is
// unambiguous about which clock produced it.
const RECEIVED_FMT_FULL = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'long',
});

// A minute or two of clock skew between the browser and whatever stamped the
// row is not a data defect. Hours are.
const FUTURE_TOLERANCE_MS = 2 * 60 * 1000;

function formatConfidence(val) {
  if (val == null) return { text: '—', cls: 'text-boss-text-muted' };
  const pct = Math.round(val * 100);
  // Use the "-text" status variables, not the raw --boss-warning/danger/success
  // fills — those are tuned for chip backgrounds, not small text, and the
  // fixed Tailwind amber-400/red-400/green-400 they used to be is illegible
  // in light mode.
  let cls = 'text-[var(--boss-success-text)]';
  if (val < 0.7) cls = 'text-[var(--boss-danger-text)]';
  else if (val < 0.85) cls = 'text-[var(--boss-warning-text)]';
  return { text: `${pct}%`, cls };
}

/**
 * The Received column.
 *
 * Shows the absolute local time as the value and keeps the relative distance
 * as a tooltip hint. A future timestamp is not smoothed over: it gets the
 * same absolute rendering plus a visible marker, because the row really is
 * wrong and hiding that only moves the confusion somewhere less fixable.
 */
function ReceivedCell({ iso }) {
  if (!iso) return <span className="text-boss-text-muted">—</span>;

  const date = parseUtc(iso);
  if (!date || Number.isNaN(date.getTime())) {
    // Show the raw value rather than swallowing it — an unparseable
    // received_at is itself the interesting thing about the row.
    return (
      <span className="text-boss-text-muted" title="Unrecognised timestamp format">
        {iso}
      </span>
    );
  }

  const isFuture = date.getTime() - Date.now() > FUTURE_TOLERANCE_MS;

  // Relative stays secondary, and never as a suffixed phrase when the value is
  // ahead of now: "in about 7 hours" is a prediction, "about 7 hours ahead of
  // now" is a measurement of how wrong the row is.
  const relative = isFuture
    ? `${formatDistanceToNow(date)} ahead of now — this has not happened yet`
    : formatDistanceToNow(date, { addSuffix: true });

  return (
    <span
      className="inline-flex items-center gap-1"
      title={`${RECEIVED_FMT_FULL.format(date)} · ${relative}`}
    >
      {isFuture && (
        <AlertTriangle
          size={12}
          role="img"
          aria-label="Received timestamp is in the future"
          className="text-[var(--boss-warning-text)] shrink-0"
        />
      )}
      <span>{RECEIVED_FMT.format(date)}</span>
    </span>
  );
}

/**
 * Table columns in render order. `width` is the default in px; the user can
 * drag any header edge to change it (double-click the edge to restore this
 * default); a page refresh restores every default. The <td>s below are
 * written by hand in the same order — keep the two in step.
 */
// Defaults sum to 1,560px so the whole set fits a ~1,600px-wide card without
// horizontal scroll. The table's min-width is this sum, so every px added to
// one default must come out of another or the scrollbar comes back.
const COLUMNS = [
  { key: 'received', label: 'Received', width: 170, sortField: 'received_at' },
  { key: 'status', label: 'Status', width: 125 },
  { key: 'distro', label: 'Distro', width: 85 },
  { key: 'sender_domain', label: 'Sender domain', width: 150 },
  { key: 'subject', label: 'Subject', width: 265 },
  { key: 'dept', label: 'Dept', width: 120 },
  { key: 'ticket_type', label: 'Ticket type', width: 130 },
  { key: 'owner', label: 'Owner', width: 140 },
  {
    key: 'label',
    label: 'Label',
    width: 100,
    title: 'Whether a human has said what this decision should have been, and whether the AI matched',
  },
  { key: 'confidence', label: 'Confidence', width: 110, sortField: 'ai_confidence' },
  { key: 'flags', label: 'Flags', width: 165 },
];
const COLUMN_DEFAULT_WIDTHS = Object.fromEntries(COLUMNS.map((c) => [c.key, c.width]));

/**
 * The always-visible 1px divider tells the user columns can be resized; the
 * wider transparent strip around it is the actual grab target, so the cursor
 * does not have to land on a single pixel.
 */
function ColumnResizeHandle({ label, onMouseDown, onDoubleClick, showDivider = true }) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
      title="Drag to resize · double-click to reset"
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      className="group absolute right-0 top-0 h-full w-2.5 flex justify-end cursor-col-resize select-none"
    >
      <span
        className={`h-full w-px transition-all group-hover:w-0.5 group-hover:bg-boss-accent ${
          showDivider ? 'bg-boss-border-hover' : 'bg-transparent'
        }`}
      />
    </span>
  );
}

const STATUS_META = {
  auto_routed: { label: 'Auto-routed', cls: 'boss-chip-success' },
  manual_review: { label: 'Manual review', cls: 'boss-chip-warning' },
  pending: { label: 'Pending', cls: 'boss-chip-neutral' },
  failed: { label: 'Failed', cls: 'boss-chip-danger' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return <span className="text-boss-text-muted">—</span>;
  return (
    <span className={`boss-chip ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

// The text a free-text search matches against for one row. Built from what
// the columns actually SHOW, not the raw stored values — searching "auto"
// has to hit a row whose status is displayed as "Auto-routed" even though
// the stored value is the enum `auto_routed`. Received/date is deliberately
// left out: the From/To pickers already filter on it, and a second, looser
// text match on the same column would just be a confusing second way to do
// the same job.
function decisionSearchText(d) {
  return [
    STATUS_META[d.status]?.label,
    d.distro,
    d.sender_domain,
    d.subject,
    d.ai_department,
    d.ai_ticket_type,
    d.ai_owner,
  ]
    .filter(Boolean)
    .join(' \u0000 ') // a separator no real value contains, so "orders" typed
    // by a user can't accidentally bridge two adjacent fields into a false
    // match (e.g. the end of a subject plus the start of a distro name).
    .toLowerCase();
}

function LabelStateCell({ decision: d }) {
  const labelled = Boolean(
    d.human_labeled_at || d.human_department || d.human_ticket_type || d.human_owner,
  );
  if (!labelled) {
    return (
      <span className="text-boss-text-muted text-xs" title="Not yet labelled">
        —
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-[11px] text-boss-text-secondary">
      <span className="inline-flex items-center">
        <MatchIcon val={d.dept_match} field="Department" className="mr-0.5" size={13} />D
      </span>
      <span className="inline-flex items-center">
        <MatchIcon val={d.type_match} field="Ticket type" className="mr-0.5" size={13} />T
      </span>
      {/* A different story from "the AI was wrong": the human named something
          the classifier has no way to emit, so it could not have been right.
          Worth seeing without opening the row. */}
      {d.human_value_off_enum && (
        <AlertTriangle
          size={13}
          className="text-[var(--boss-warning-text)] shrink-0"
          aria-label="Human label is outside our taxonomy"
        />
      )}
    </span>
  );
}

// Timestamps sort by instant, not by string. The stored values are a mix of
// bare-UTC and offset-suffixed forms, and a lexicographic compare across those
// two shapes silently interleaves them.
const DATE_SORT_FIELDS = new Set(['received_at', 'decided_at', 'human_labeled_at']);

/**
 * Sort key for a row, or null when the field is missing.
 *
 * Missing values sort last in BOTH directions. Flipping the direction to hunt
 * for the least-confident decisions should not first hand you a screenful of
 * rows that carry no confidence at all.
 */
function sortKey(row, field) {
  const raw = row?.[field];
  if (raw == null || raw === '') return null;
  if (DATE_SORT_FIELDS.has(field)) {
    const t = parseUtc(raw)?.getTime();
    return Number.isFinite(t) ? t : null;
  }
  return raw;
}

export default function DecisionsTab() {
  const { hasPermission, isAdmin } = usePermissions();
  const canLabel = isAdmin() || hasPermission('ai_dispatcher.decisions.label');

  const [decisions, setDecisions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [distroFilter, setDistroFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Text search, unlike the four filters above, never touches the network —
  // it narrows the set the server already returned. See `decisionSearchText`
  // for exactly what it matches against.
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination — see the note on fetchDecisions for why a page is a local
  // slice rather than a request.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(loadPageSize);

  // Sort
  const [sortField, setSortField] = useState('received_at');
  const [sortDir, setSortDir] = useState('desc');

  // Drawer. Tracks a position within the CURRENT PAGE rather than a decision
  // object, so J/K walks exactly what is on screen, and so a row refreshed by
  // a label write is picked up from state instead of a stale copy captured at
  // click time.
  const [selectedIndex, setSelectedIndex] = useState(null);

  // The decisions list deliberately does not refetch on every label write —
  // reordering the table under someone who is labelling a run of rows in
  // sequence would lose their place. The list is refreshed on drawer close
  // instead.
  const labelledThisSessionRef = useRef(false);

  /**
   * Paging is a client-side slice of ONE server fetch, not a request per page.
   * That runs against the usual preference for server-side paging, and it is
   * deliberate.
   *
   * `GET /ai-dispatcher/decisions` does support skip/limit (`offset`/`limit`,
   * clamped to 500 server-side) — but it sorts on a hardcoded `decided_at: -1`
   * and accepts no sort parameter. This table lets you sort by Received and by
   * Confidence. Fetch 15 rows a page and the sort can only reorder those 15:
   * clicking "Confidence" would surface the least-confident of the newest
   * fifteen rather than the least-confident decisions, which is the precise
   * question this screen exists to answer. Wrong-but-fast is the worse trade
   * on a review screen, so we pull the whole filtered set and sort it here.
   *
   * This holds while the filtered set fits in one request. Past FETCH_LIMIT the
   * tail is genuinely unreachable, and the banner below says so instead of
   * implying the sort is global. The real fix is a `sort` / `sort_dir` param on
   * the list endpoint; until that exists, narrowing the date range is the
   * escape hatch.
   */
  const fetchDecisions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', FETCH_LIMIT);
      if (distroFilter) params.set('distro', distroFilter);
      if (deptFilter) params.set('department', deptFilter);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);

      const res = await api.get(`${API_URL}/api/ai-dispatcher/decisions?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to load decisions');

      const rows = Array.isArray(data?.decisions) ? data.decisions : [];
      setDecisions(rows);
      setTotal(data?.total ?? rows.length);
      // Any refetch reshuffles what index N points at, so a held selection is
      // no longer trustworthy.
      setSelectedIndex(null);
    } catch (err) {
      setError(err?.message || 'Failed to load decisions');
    } finally {
      setLoading(false);
    }
  }, [distroFilter, deptFilter, fromDate, toDate]);

  useEffect(() => {
    fetchDecisions();
  }, [fetchDecisions]);

  // Same reasoning as the sort below: the server already returned the whole
  // filtered set, so narrowing it further by text is a local operation, not
  // a new request. Computed once per keystroke's worth of re-render (no
  // debounce needed — this is a string scan over at most FETCH_LIMIT rows,
  // not a query), and it runs BEFORE sort so a search doesn't need to know
  // anything about sort order.
  const searchedDecisions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return decisions;
    return decisions.filter((d) => decisionSearchText(d).includes(q));
  }, [decisions, searchQuery]);

  // Sort spans the whole loaded set, not the visible page — that is the point
  // of loading it in one go. It is also local, so a sort click no longer costs
  // a round trip or a spinner.
  const sortedDecisions = useMemo(() => {
    return [...searchedDecisions].sort((a, b) => {
      const aKey = sortKey(a, sortField);
      const bKey = sortKey(b, sortField);
      if (aKey == null && bKey == null) return 0;
      if (aKey == null) return 1;
      if (bKey == null) return -1;
      const cmp =
        typeof aKey === 'number' && typeof bKey === 'number'
          ? aKey - bKey
          : String(aKey).localeCompare(String(bKey));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [searchedDecisions, sortField, sortDir]);

  const loadedCount = sortedDecisions.length;
  const totalPages = Math.max(1, Math.ceil(loadedCount / pageSize));
  // Clamp rather than correct in an effect: a refetch that shrinks the result
  // set (a label write, a narrower filter) would otherwise leave `page` past
  // the end and render an empty table under a "Showing 61–75 of 12" readout.
  const safePage = Math.min(Math.max(1, page), totalPages);

  const pageRows = useMemo(
    () => sortedDecisions.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sortedDecisions, safePage, pageSize],
  );

  const selectedDecision = selectedIndex == null ? null : pageRows[selectedIndex] ?? null;

  const goToOffset = useCallback(
    (delta) => {
      setSelectedIndex((i) => {
        if (i == null) return i;
        const next = i + delta;
        return next >= 0 && next < pageRows.length ? next : i;
      });
    },
    [pageRows.length],
  );

  /**
   * A label write came back. The PATCH returns the full decision including the
   * server-computed dept_match / type_match, so splice it in rather than
   * recomputing agreement on the client — the server is the only thing that
   * decides what "match" means.
   *
   * A null `updated` means the response body was not a recognisable decision;
   * fall back to a full refetch rather than leaving the row showing pre-write
   * state.
   */
  const handleLabeled = useCallback(
    (updated) => {
      labelledThisSessionRef.current = true;

      if (!updated) {
        fetchDecisions();
        return;
      }
      setDecisions((rows) =>
        rows.map((r) => (r.dispatch_item_id === updated.dispatch_item_id ? updated : r)),
      );
    },
    [fetchDecisions],
  );

  // Stable identities — LabelPanel re-subscribes its keyboard listener
  // whenever these change.
  const handleNext = useCallback(() => goToOffset(1), [goToOffset]);
  const handlePrev = useCallback(() => goToOffset(-1), [goToOffset]);

  const handleCloseDrawer = useCallback(() => {
    setSelectedIndex(null);
    // Decisions are upserted on (tenant_id, message_id) by the n8n workflow,
    // so rows change underneath a long labelling session. Pull fresh data once
    // the drawer is out of the way, where a reorder costs nobody their place.
    if (labelledThisSessionRef.current) {
      labelledThisSessionRef.current = false;
      fetchDecisions();
    }
  }, [fetchDecisions]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    // Reordering the whole set makes "page 4" meaningless — it is a different
    // slice of different rows. Land back at the top of the new order.
    setPage(1);
    setSelectedIndex(null);
  };

  const handlePageChange = useCallback((next) => {
    setPage(next);
    // selectedIndex is an index into the visible page, so it cannot survive a
    // page change.
    setSelectedIndex(null);
  }, []);

  const handlePageSizeChange = useCallback((next) => {
    setPageSize(next);
    setPage(1);
    setSelectedIndex(null);
    savePageSize(next);
  }, []);

  const resetToFirstPage = () => {
    setPage(1);
    setSelectedIndex(null);
  };

  const { widths: columnWidths, startResize, resetColumn } =
    useResizableColumns(COLUMN_DEFAULT_WIDTHS);
  const tableMinWidth = COLUMNS.reduce((sum, c) => sum + columnWidths[c.key], 0);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ChevronUp size={14} className="inline ml-1" />
    ) : (
      <ChevronDown size={14} className="inline ml-1" />
    );
  };

  const hasActiveFilters = Boolean(distroFilter || deptFilter || fromDate || toDate || searchQuery);
  const isTruncated = total > loadedCount;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-boss-text">Routing Decisions</h2>
        <p className="text-sm text-boss-text-secondary mt-0.5">
          Every AI dispatch decision, in shadow mode.{' '}
          {canLabel && (
            <span>
              Open a decision to record what it should have been — that is what the agreement
              rate on the Analytics tab is measured against.{' '}
            </span>
          )}
          {total > 0 && <span>Total: {total.toLocaleString()}</span>}
        </p>
      </div>

      {/* Shadow-mode banner — unmissable by design. This is the whole point
          of the review screen: nothing shown here has been acted on, and it
          must not read as "tickets were created."

          NO TENANT NAMED. This said "has been sent to AppDirect Provider
          Sales" until 16 Oct 2026, hardcoded, on a screen every dispatcher
          tenant sees — so Johnny Bravo Corp was told their decisions had not
          reached a different customer's sales team. Written when AppDirect
          was the only tenant with the Dispatcher, which stopped being true.

          The destination was never load-bearing here. What the banner has to
          convey is "nothing happened", and it does that better without a
          name that is wrong for everyone but one tenant. */}
      <div className="boss-alert-warning rounded-xl p-4 flex items-start gap-3">
        <ShieldAlert size={20} className="shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-boss-text">Shadow mode — for review only</p>
          <p className="text-sm text-boss-text-secondary mt-0.5">
            These are the AI's classification decisions, shown for human review. Nothing on this
            page has been sent anywhere or created a real ticket.
          </p>
        </div>
      </div>

      {/* Truncation notice. Only fires once the filtered set outgrows a single
          request — calling a partial set "sorted" without saying which part is
          the thing that would actually mislead. */}
      {!loading && !error && isTruncated && (
        <div className="boss-alert-warning rounded-xl p-3 flex items-start gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <p className="text-xs text-boss-text-secondary">
            Showing the {loadedCount.toLocaleString()} most recent decisions of{' '}
            {total.toLocaleString()}. Sorting and paging cover these only — narrow the date
            range to reach older decisions.
          </p>
        </div>
      )}

      {/* Decisions — the filter strip and the result set share one card so the
          two read as one control surface instead of two floating panels.
          The filters sit OUTSIDE the `loadedCount > 0` guard on purpose: nested
          under it, a filter combo matching nothing would unmount the controls and
          the Clear button at exactly the moment the user needs them, leaving a
          page reload as the only way out of a dead end. */}
      <div className="glass-card overflow-hidden">
        {/* Filters */}
        <div className="p-4 border-b border-boss-border">
          <div className="flex flex-wrap gap-3 items-end">
            {/* htmlFor/id on all four: these were bare <label>s, which render
                the same but leave every filter control unnamed to a screen
                reader — four unlabelled comboboxes in a row. */}
            <div>
              <label htmlFor="decisions-filter-distro" className="block text-xs text-boss-text-secondary mb-1">
                Distro
              </label>
              <select
                id="decisions-filter-distro"
                className="boss-input h-7 px-2 w-35"
                value={distroFilter}
                onChange={(e) => { setDistroFilter(e.target.value); resetToFirstPage(); }}
              >
                {DISTRO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="decisions-filter-department" className="block text-xs text-boss-text-secondary mb-1">
                Department
              </label>
              <select
                id="decisions-filter-department"
                className="boss-input h-7 px-2 w-44"
                value={deptFilter}
                onChange={(e) => { setDeptFilter(e.target.value); resetToFirstPage(); }}
              >
                <option value="">All departments</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="decisions-filter-from" className="block text-xs text-boss-text-secondary mb-1">
                From
              </label>
              <input
                id="decisions-filter-from"
                type="date"
                className="boss-input h-7 px-2"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); resetToFirstPage(); }}
              />
            </div>
            <div>
              <label htmlFor="decisions-filter-to" className="block text-xs text-boss-text-secondary mb-1">
                To
              </label>
              <input
                id="decisions-filter-to"
                type="date"
                className="boss-input h-7 px-2"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); resetToFirstPage(); }}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              {/* The slot stretches to fill the row; this inner box takes 60%
                  of it and ml-auto pushes it to the right edge. Label, icon
                  and input move as one so the absolutely-positioned icon
                  stays inside the input. */}
              <div className="w-3/5 ml-auto">
                <label htmlFor="decisions-filter-search" className="block text-xs text-boss-text-secondary mb-1">
                  Search
                </label>
                {/* Status/Distro/Sender domain/Subject/Dept/Ticket type/Owner —
                    see decisionSearchText for exactly what this matches
                    against. Local to the already-loaded set, so this updates
                    on every keystroke rather than waiting on a request. */}
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-boss-text-muted pointer-events-none"
                  />
                  <input
                    id="decisions-filter-search"
                    type="text"
                    placeholder="Status, distro, sender, subject, dept, ticket type, owner…"
                    className="boss-input h-9 px-2 pl-8 w-full"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); resetToFirstPage(); }}
                  />
                </div>
              </div>
            </div>
            {hasActiveFilters && (
              <button
                className="btn-secondary text-xs"
                onClick={() => {
                  setDistroFilter('');
                  setDeptFilter('');
                  setFromDate('');
                  setToDate('');
                  setSearchQuery('');
                  resetToFirstPage();
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="boss-alert-danger m-4 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-boss-text">{error}</p>
              <button onClick={fetchDecisions} className="text-xs text-violet-400 hover:text-violet-300 mt-1">
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Empty state — distinguish "nothing has ever landed" from "your
            filters don't match anything," so an active filter combo doesn't
            read as a broken/unconfigured workflow. */}
        {!loading && !error && loadedCount === 0 && (
          <div className="p-12 text-center">
            <Filter size={36} className="mx-auto text-boss-text-muted mb-3" />
            {hasActiveFilters ? (
              <>
                <p className="text-boss-text font-medium">No decisions match these filters</p>
                <p className="text-sm text-boss-text-secondary mt-1 max-w-sm mx-auto">
                  Try widening the date range or clearing a filter.
                </p>
              </>
            ) : (
              <>
                <p className="text-boss-text font-medium">No decisions yet</p>
                <p className="text-sm text-boss-text-secondary mt-1 max-w-sm mx-auto">
                  The n8n workflow will start posting routing decisions here once it's enabled.
                </p>
              </>
            )}
          </div>
        )}

        {/* Table */}
        {!loading && !error && loadedCount > 0 && (
          <div className="overflow-x-auto">
            {/* table-fixed: column widths come only from the <col>s, never
                from cell content, which is what makes them draggable. The
                min-width is the sum of the columns so a wide set scrolls
                inside overflow-x-auto; when the set is narrower than the card,
                the trailing width-less column soaks up the slack instead of
                the browser spreading it across (and fighting) every column. */}
            <table className="w-full table-fixed text-sm" style={{ minWidth: tableMinWidth }}>
              <colgroup>
                {COLUMNS.map((c) => (
                  <col key={c.key} style={{ width: columnWidths[c.key] }} />
                ))}
                <col />
              </colgroup>
              <thead>
                <tr className="border-b border-boss-border">
                  {COLUMNS.map((c, i) => (
                    <th
                      key={c.key}
                      title={c.title}
                      onClick={c.sortField ? () => handleSort(c.sortField) : undefined}
                      className={`relative px-3 py-3 text-boss-text-secondary font-medium truncate ${
                        c.align === 'right' ? 'text-right' : 'text-left'
                      } ${c.sortField ? 'cursor-pointer hover:text-boss-text select-none' : ''}`}
                    >
                      {c.label}
                      {c.sortField && <SortIcon field={c.sortField} />}
                      <ColumnResizeHandle
                        label={c.label}
                        onMouseDown={startResize(c.key)}
                        onDoubleClick={() => resetColumn(c.key)}
                        // Nothing sits to the right of the last column but the
                        // filler, so a resting divider there reads as a stray
                        // line. Still draggable; the line shows on hover.
                        showDivider={i < COLUMNS.length - 1}
                      />
                    </th>
                  ))}
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((d, idx) => {
                  const conf = formatConfidence(d.ai_confidence);
                  const flags = Array.isArray(d.flags) ? d.flags : [];
                  return (
                    <tr
                      key={d.dispatch_item_id}
                      onClick={() => setSelectedIndex(idx)}
                      // Rows are the entry point to the labelling flow, so
                      // they have to be reachable without a mouse.
                      tabIndex={0}
                      role="button"
                      aria-label={`Open decision: ${d.subject || 'no subject'}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedIndex(idx);
                        }
                      }}
                      className="border-b border-boss-border hover:bg-boss-elevated focus:bg-boss-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-violet-500 cursor-pointer transition-colors last:border-0"
                    >
                      {/* Every cell clips to its column (`truncate` =
                          nowrap + overflow-hidden + ellipsis); under
                          table-fixed, unclipped text would spill over the
                          neighbouring column instead of widening its own.
                          `title` gives the full value back on hover. */}
                      <td className="px-3 py-3 text-boss-text-secondary truncate text-xs">
                        <ReceivedCell iso={d.received_at} />
                      </td>
                      <td className="px-3 py-3 truncate">
                        <StatusBadge status={d.status} />
                      </td>
                      <td className="px-3 py-3 text-boss-text-secondary text-xs truncate" title={d.distro || undefined}>
                        {d.distro || '—'}
                      </td>
                      <td className="px-3 py-3 text-boss-text-secondary text-xs truncate" title={d.sender_domain || undefined}>
                        {d.sender_domain || '—'}
                      </td>
                      <td className="px-3 py-3 text-boss-text text-sm truncate" title={d.subject || undefined}>
                        {d.subject}
                      </td>
                      <td className="px-3 py-3 text-boss-text-secondary text-xs truncate" title={d.ai_department || undefined}>
                        {d.ai_department || (
                          <span title="The AI produced no department">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-boss-text-secondary text-xs truncate" title={d.ai_ticket_type || undefined}>
                        {d.ai_ticket_type || (
                          <span title="The AI produced no ticket type">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-boss-text-secondary text-xs truncate" title={d.ai_owner || undefined}>
                        {d.ai_owner || (
                          <span title="The AI assigned no owner">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 truncate">
                        <LabelStateCell decision={d} />
                      </td>
                      <td className={`px-3 py-3 font-medium text-xs truncate ${conf.cls}`}>
                        {conf.text}
                      </td>
                      <td className="px-3 py-3 overflow-hidden">
                        <FlagsBadgeCluster flags={flags} columnWidth={columnWidths.flags} />
                      </td>
                      <td aria-hidden="true" />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rendered even at a single page — it carries the rows-per-page
          control and the X–Y of Z readout, both worth having before the
          list has grown. `total` here is the LOADED set, so the readout can
          never name a range the buttons cannot reach; the server-side count
          lives in the header and in the truncation banner above. */}
      {!loading && !error && loadedCount > 0 && (
        <PaginationControls
          page={safePage}
          pageSize={pageSize}
          total={loadedCount}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          testid="decisions-pagination"
        />
      )}

      {/* Decision detail drawer */}
      {selectedDecision && (
        <DecisionDrawer
          decision={selectedDecision}
          onClose={handleCloseDrawer}
          canLabel={canLabel}
          onLabeled={handleLabeled}
          onNext={selectedIndex < pageRows.length - 1 ? handleNext : undefined}
          onPrev={selectedIndex > 0 ? handlePrev : undefined}
          position={{ index: selectedIndex, total: pageRows.length }}
        />
      )}
    </div>
  );
}

const FLAG_CHIP_GAP_PX = 4; // gap-1

// No max-width: a chip shows its full text whenever the column has room, and
// only the first chip ever truncates — when even it alone does not fit.
const flagChipClass = (f) =>
  `boss-chip text-[10px] ${isCriticalFlag(f) ? 'boss-chip-danger' : 'boss-chip-warning'}`;

/**
 * As many full-width flag chips as fit on one line of the Flags column, with
 * the rest folded into a "+N" chip — so widening the column first un-truncates
 * a long flag, then reveals more of them.
 *
 * Chip widths depend on the flag text, so they are measured, not guessed: an
 * invisible, absolutely positioned copy of every chip (plus a worst-case "+N")
 * is laid out at natural width, and a layout effect counts how many fit
 * before the browser paints. `columnWidth` is only a re-measure trigger; the
 * cell's real width is read from the DOM.
 */
function FlagsBadgeCluster({ flags, columnWidth }) {
  const wrapperRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(Math.min(flags.length, 2));
  // Per-row: "+N" opens this cell only, wrapping every flag onto extra lines.
  const [expanded, setExpanded] = useState(false);
  const flagsKey = flags.join('|');

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const measure = measureRef.current;
    if (!wrapper || !measure) return;
    const available = wrapper.getBoundingClientRect().width;
    // Query by attribute, not `.children`: the dev build's visual-editor
    // plugin wraps .map() output in a `display: contents` span, which has no
    // box and would measure as a single 0px "chip".
    const plusWidth = measure.querySelector('[data-measure-plus]').getBoundingClientRect().width;
    const widths = Array.from(measure.querySelectorAll('[data-measure-chip]')).map(
      (n) => n.getBoundingClientRect().width,
    );

    let used = 0;
    let count = 0;
    for (let i = 0; i < widths.length; i += 1) {
      const next = used + (i ? FLAG_CHIP_GAP_PX : 0) + widths[i];
      const remaining = widths.length - (i + 1);
      const needed = remaining ? next + FLAG_CHIP_GAP_PX + plusWidth : next;
      // Always show at least one; it truncates if even that is too wide.
      if (needed > available && count > 0) break;
      used = next;
      count += 1;
    }
    setVisibleCount(count);
  }, [flagsKey, columnWidth]);

  if (flags.length === 0) return null;
  const rest = flags.length - visibleCount;
  const shown = expanded ? flags : flags.slice(0, visibleCount);

  // The row opens the decision drawer on click and on Enter/Space keydown.
  // Stop both from bubbling so the toggle only toggles: the row's keydown
  // handler calls preventDefault, which would cancel the button's own
  // Enter-to-click activation.
  const toggle = (e) => {
    e.stopPropagation();
    setExpanded((x) => !x);
  };
  const stopKeys = (e) => e.stopPropagation();

  return (
    <div ref={wrapperRef} className="relative">
      <div className={`flex gap-1 ${expanded ? 'flex-wrap' : 'whitespace-nowrap'}`}>
        {/* boss-chip is inline-flex, so `truncate` on the chip clips without
            ever drawing an ellipsis — the text is an anonymous flex item.
            Truncating an inner span gives the ellipsis back on flags like
            `ATTACHMENT_IMAGES_SIZE_FILTERED:5`. */}
        {shown.map((f) => (
          <span key={f} title={f} className={`${flagChipClass(f)} min-w-0 max-w-full`}>
            <span className="truncate">{f}</span>
          </span>
        ))}
        {(expanded || rest > 0) && (
          <button
            type="button"
            onClick={toggle}
            onKeyDown={stopKeys}
            aria-expanded={expanded}
            aria-label={expanded ? 'Show fewer flags' : `Show ${rest} more flag${rest === 1 ? '' : 's'}`}
            title={expanded ? 'Show fewer' : flags.slice(visibleCount).join(', ')}
            className="boss-chip text-[10px] boss-chip-neutral shrink-0 cursor-pointer hover:text-boss-text focus:outline-none focus-visible:ring-1 focus-visible:ring-violet-500"
          >
            {expanded ? 'less' : `+${rest}`}
          </button>
        )}
      </div>
      <div
        ref={measureRef}
        aria-hidden="true"
        className="invisible pointer-events-none absolute left-0 top-0 flex gap-1 whitespace-nowrap"
      >
        {flags.map((f) => (
          <span key={f} data-measure-chip className={`${flagChipClass(f)} shrink-0`}>
            <span className="truncate">{f}</span>
          </span>
        ))}
        <span data-measure-plus className="boss-chip text-[10px] boss-chip-neutral shrink-0">
          +{flags.length}
        </span>
      </div>
    </div>
  );
}
